import React, { useState } from 'react';

/**
 * Transit (Gochar) Display Component
 * Shows current planetary transits and their effects
 */
const TransitDisplay = ({ data }) => {
  const [expandedSection, setExpandedSection] = useState('positions');

  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Transit data available</div>;
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Format date
  const currentDate = data.current_datetime ? new Date(data.current_datetime).toLocaleString() : 'N/A';

  // Planet colors
  const planetColors = {
    'Sun': 'text-orange-600',
    'Moon': 'text-blue-400',
    'Mars': 'text-red-600',
    'Mercury': 'text-green-600',
    'Jupiter': 'text-yellow-600',
    'Venus': 'text-pink-600',
    'Saturn': 'text-gray-700',
    'Rahu': 'text-purple-600'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-xl text-white">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">🌌</span>
          <h2 className="text-2xl font-bold">Current Transits (Gochar)</h2>
        </div>
        <p className="text-indigo-100">Real-time planetary positions and their effects</p>
        <div className="mt-3 text-sm text-indigo-200">
          Last Updated: {currentDate}
        </div>
      </div>

      {/* Current Planetary Positions */}
      {data.current_positions && Object.keys(data.current_positions).length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('positions')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">🪐 Current Planetary Positions</h3>
            <span className="text-2xl">{expandedSection === 'positions' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'positions' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(data.current_positions).map(([planet, pos]) => (
                <div key={planet} className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200">
                  <div className={`font-bold text-lg mb-2 ${planetColors[planet] || 'text-gray-700'}`}>
                    {planet}
                    {pos.retrograde && <span className="ml-2 text-red-500 text-sm">(R)</span>}
                  </div>
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-gray-600">Sign:</span>
                      <span className="ml-2 font-semibold">{pos.sign}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Longitude:</span>
                      <span className="ml-2">{pos.longitude?.toFixed(2)}°</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Speed:</span>
                      <span className="ml-2">{pos.speed?.toFixed(4)}°/day</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transit-Natal Aspects */}
      {data.transit_natal_aspects && data.transit_natal_aspects.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('aspects')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">
              ⚡ Transit-Natal Aspects ({data.transit_natal_aspects.length})
            </h3>
            <span className="text-2xl">{expandedSection === 'aspects' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'aspects' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
                  <tr>
                    <th className="p-3 text-left">Transit Planet</th>
                    <th className="p-3 text-left">Aspect</th>
                    <th className="p-3 text-left">Natal Planet</th>
                    <th className="p-3 text-left">Orb</th>
                    <th className="p-3 text-left">Nature</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transit_natal_aspects.slice(0, 20).map((aspect, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 font-semibold text-indigo-600">{aspect.transit_planet}</td>
                      <td className="p-3">{aspect.aspect_type}</td>
                      <td className="p-3 font-semibold text-purple-600">{aspect.natal_planet}</td>
                      <td className="p-3 text-sm">{aspect.orb?.toFixed(2)}°</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          aspect.nature === 'harmonious' ? 'bg-green-100 text-green-700' :
                          aspect.nature === 'challenging' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {aspect.nature}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Major Ingresses */}
      {data.major_ingresses && data.major_ingresses.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-cyan-500">
          <h3 className="text-xl font-bold mb-4 text-gray-800">🚀 Upcoming Sign Changes</h3>
          <div className="space-y-3">
            {data.major_ingresses.map((ingress, idx) => (
              <div key={idx} className="p-4 bg-cyan-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-cyan-700">{ingress.planet}</span>
                    <span className="mx-2">→</span>
                    <span className="font-semibold text-cyan-600">{ingress.to_sign}</span>
                  </div>
                  <div className="text-sm text-gray-600">{ingress.date || 'Date TBD'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retrograde Events */}
      {data.retrograde_events && data.retrograde_events.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-500">
          <h3 className="text-xl font-bold mb-4 text-gray-800">🔄 Upcoming Retrograde Events</h3>
          <div className="space-y-3">
            {data.retrograde_events.map((retro, idx) => (
              <div key={idx} className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-red-700">{retro.planet}</span>
                    <span className="mx-2">{retro.event_type}</span>
                  </div>
                  <div className="text-sm text-gray-600">{retro.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SAV Transit Assessments */}
      {data.sav_transit_assessments && Object.keys(data.sav_transit_assessments).length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <button
            onClick={() => toggleSection('sav')}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-bold text-gray-800">📊 Ashtakavarga Transit Strength</h3>
            <span className="text-2xl">{expandedSection === 'sav' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'sav' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data.sav_transit_assessments).map(([planet, assessment]) => (
                <div key={planet} className="p-4 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg">
                  <div className={`font-bold mb-2 ${planetColors[planet] || 'text-gray-700'}`}>
                    {planet}
                  </div>
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-gray-600">Transit Sign:</span>
                      <span className="ml-2 font-semibold">{assessment.transit_sign || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">SAV Score:</span>
                      <span className="ml-2 font-semibold">{assessment.sav_score || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Strength:</span>
                      <span className={`ml-2 font-semibold ${
                        assessment.strength === 'Strong' ? 'text-green-600' :
                        assessment.strength === 'Weak' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {assessment.strength || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Significant Upcoming Dates */}
      {data.significant_upcoming_dates && data.significant_upcoming_dates.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-xl font-bold mb-4 text-gray-800">📅 10-Day Forecast</h3>
          <div className="space-y-2">
            {data.significant_upcoming_dates.map((event, idx) => (
              <div key={idx} className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-semibold text-indigo-700">{event.date}</span>
                  <span className="mx-2 text-gray-400">•</span>
                  <span className="text-gray-700">{event.event}</span>
                </div>
                <span className="text-xs text-gray-500">{event.significance || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransitDisplay;
